import { useState, useEffect } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Church, Mail, Lock, User, Ticket, CheckCircle, XCircle, Loader2, Shield, Heart, Users } from "lucide-react";
import { registerUser, validateInvite, processSession } from "@/lib/api";
import { toast } from "sonner";
import { getLiturgicalSeason, getSeasonLogo } from "@/lib/liturgicalCalendar";
import PublicNavigation from "@/components/PublicNavigation";

export default function RegisterPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [inviteCode, setInviteCode] = useState(searchParams.get("invite") || "");
  const [inviteValid, setInviteValid] = useState(null); // null = not checked, true/false = result
  const [inviteChecking, setInviteChecking] = useState(false);
  const [inviterName, setInviterName] = useState("");
  const [parishName, setParishName] = useState("");
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [loading, setLoading] = useState(false);
  const [season] = useState(() => getLiturgicalSeason());

  // Validate invite code when it changes
  useEffect(() => {
    if (inviteCode.length >= 10) {
      checkInviteCode(inviteCode);
    } else {
      setInviteValid(null);
      setInviterName("");
      setParishName("");
    }
  }, [inviteCode]);

  const checkInviteCode = async (code) => {
    setInviteChecking(true);
    try {
      const res = await validateInvite(code);
      setInviteValid(true);
      setInviterName(res.data.inviter_name || "");
      setParishName(res.data.parish_name || "");
    } catch (err) {
      setInviteValid(false);
      setInviterName("");
      setParishName("");
    }
    setInviteChecking(false);
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    if (!inviteValid) {
      toast.error("Please enter a valid invite code");
      return;
    }
    if (!agreedToTerms) {
      toast.error("Please agree to the Terms of Service and Privacy Policy");
      return;
    }
    if (password.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    setLoading(true);
    try {
      const res = await registerUser({ email, password, name, invite_code: inviteCode });
      localStorage.setItem("token", res.data.token);
      localStorage.setItem("user", JSON.stringify(res.data.user));
      // Set flag to trigger beta notice on first dashboard visit after registration
      sessionStorage.setItem('parish_just_logged_in', 'true');
      toast.success("Welcome to Parish!");
      navigate("/dashboard", { state: { user: res.data.user } });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Registration failed");
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleRegister = () => {
    if (!inviteValid) {
      toast.error("Please enter a valid invite code first");
      return;
    }
    // Store invite code in sessionStorage for OAuth callback
    sessionStorage.setItem("pending_invite_code", inviteCode);
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen bg-[#F9F7F2]">
      <PublicNavigation />
      
      <div className="flex items-center justify-center px-4 py-12">
        <div className="w-full max-w-md animate-fade-in">
          <div className="flex items-center justify-center gap-3 mb-10">
            <img 
              src={getSeasonLogo(season)} 
              alt="Parish" 
              className="w-11 h-11 rounded-lg transition-all duration-500"
            />
            <span className="font-['Crimson_Text'] text-3xl font-bold text-[#2D2A26] tracking-tight">Parish</span>
          </div>

          <Card className="bg-white border-[#E5E0D5] shadow-lg" data-testid="register-card">
          <CardHeader className="pb-4">
            <CardTitle className="font-['Crimson_Text'] text-2xl text-[#2D2A26]">Join the Community</CardTitle>
            <CardDescription className="font-['Nunito'] text-[#6B665E]">
              Parish is invite-only. Enter your invite code to get started.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Membership Explanation - Show when no valid invite */}
            {inviteValid !== true && (
              <div className="space-y-4 pb-4 border-b border-[#E5E0D5]">
                <p className="text-sm text-[#2D2A26] font-['Nunito'] leading-relaxed">
                  Parish is a community built on trust. To protect the integrity of our community and ensure a safe space for fellowship, certain features are available only to verified members of the Catholic Church.
                </p>
                
                <div className="bg-[#F9F7F2] rounded-lg p-3">
                  <div className="flex items-start gap-2">
                    <Shield className="w-4 h-4 text-[var(--liturgical-primary)] flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-sm font-medium text-[#2D2A26] font-['Nunito']">How it works</p>
                      <p className="text-xs text-[#6B665E] font-['Nunito']">
                        To join Parish, you'll need an invitation from a current member of your parish. This helps us confirm your connection to the Catholic community and links you to your local parish family.
                      </p>
                    </div>
                  </div>
                </div>
                
                <div className="bg-[#F9F7F2] rounded-lg p-3">
                  <div className="flex items-start gap-2">
                    <Heart className="w-4 h-4 text-[var(--liturgical-primary)] flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-sm font-medium text-[#2D2A26] font-['Nunito']">Why we do this</p>
                      <p className="text-xs text-[#6B665E] font-['Nunito']">
                        By connecting members through their parishes, we foster a community rooted in shared faith and the <em>sensus fidelium</em>—the sense of the faithful. This creates a trusted environment where Catholics can connect, support one another, and grow together in faith.
                      </p>
                    </div>
                  </div>
                </div>
                
                <div className="flex items-start gap-2 text-xs text-[#6B665E] font-['Nunito'] bg-[var(--liturgical-primary)]/5 rounded-lg p-3">
                  <Users className="w-4 h-4 text-[var(--liturgical-primary)] flex-shrink-0 mt-0.5" />
                  <span>
                    If you're Catholic and would like to join, reach out to a fellow parishioner who's already a member, or ask your parish office for more information.
                  </span>
                </div>
              </div>
            )}
            
            {/* Invite Code Field - First and Required */}
            <div className="space-y-2">
              <Label className="font-['Nunito'] text-[#2D2A26] text-sm">Invite Code *</Label>
              <div className="relative">
                <Ticket className="absolute left-3 top-3 h-4 w-4 text-[#6B665E]" />
                <Input
                  type="text"
                  placeholder="Enter your invite code"
                  value={inviteCode}
                  onChange={(e) => setInviteCode(e.target.value.trim())}
                  className={`pl-10 pr-10 border-[#E5E0D5] focus-visible:ring-[var(--liturgical-primary)] font-['Nunito'] ${
                    inviteValid === true ? 'border-green-500 bg-green-50' : 
                    inviteValid === false ? 'border-red-500 bg-red-50' : ''
                  }`}
                  data-testid="register-invite-input"
                  required
                />
                <div className="absolute right-3 top-3">
                  {inviteChecking && <Loader2 className="h-4 w-4 text-[#6B665E] animate-spin" />}
                  {!inviteChecking && inviteValid === true && <CheckCircle className="h-4 w-4 text-green-600" />}
                  {!inviteChecking && inviteValid === false && <XCircle className="h-4 w-4 text-red-500" />}
                </div>
              </div>
              {inviteValid === true && inviterName && (
                <p className="text-xs text-green-600 font-['Nunito']">
                  Invited by {inviterName} to join <strong>{parishName}</strong>
                </p>
              )}
              {inviteValid === false && (
                <p className="text-xs text-red-500 font-['Nunito']">
                  Invalid or expired invite code
                </p>
              )}
            </div>

            {/* Only show registration options if invite is valid */}
            {inviteValid === true && (
              <>
                <Button
                  variant="outline"
                  className="w-full h-11 border-[#E5E0D5] text-[#2D2A26] hover:bg-[#F0EBE0] font-['Nunito'] flex items-center gap-3"
                  onClick={handleGoogleRegister}
                  data-testid="register-google-btn"
                >
                  <svg className="w-5 h-5" viewBox="0 0 24 24"><path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/><path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/><path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/><path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/></svg>
                  Continue with Google
                </Button>

                <div className="relative">
                  <div className="absolute inset-0 flex items-center"><span className="w-full border-t border-[#E5E0D5]" /></div>
                  <div className="relative flex justify-center text-xs uppercase"><span className="bg-white px-3 text-[#6B665E] font-['Nunito']">or register with email</span></div>
                </div>

                <form onSubmit={handleRegister} className="space-y-4">
                  <div className="space-y-2">
                    <Label className="font-['Nunito'] text-[#2D2A26] text-sm">Full Name</Label>
                    <div className="relative">
                      <User className="absolute left-3 top-3 h-4 w-4 text-[#6B665E]" />
                      <Input
                        type="text"
                        placeholder="Your full name"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        className="pl-10 border-[#E5E0D5] focus-visible:ring-[var(--liturgical-primary)] font-['Nunito']"
                        data-testid="register-name-input"
                        required
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label className="font-['Nunito'] text-[#2D2A26] text-sm">Email</Label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-3 h-4 w-4 text-[#6B665E]" />
                      <Input
                        type="email"
                        placeholder="you@example.com"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        className="pl-10 border-[#E5E0D5] focus-visible:ring-[var(--liturgical-primary)] font-['Nunito']"
                        data-testid="register-email-input"
                        required
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label className="font-['Nunito'] text-[#2D2A26] text-sm">Password</Label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-3 h-4 w-4 text-[#6B665E]" />
                      <Input
                        type="password"
                        placeholder="Min 6 characters"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        className="pl-10 border-[#E5E0D5] focus-visible:ring-[var(--liturgical-primary)] font-['Nunito']"
                        data-testid="register-password-input"
                        required
                      />
                    </div>
                  </div>
                  
                  {/* Terms Agreement Checkbox */}
                  <div className="flex items-start gap-3 py-2">
                    <Checkbox 
                      id="terms-agreement"
                      checked={agreedToTerms}
                      onCheckedChange={setAgreedToTerms}
                      className="mt-0.5 border-[#E5E0D5] data-[state=checked]:bg-[var(--liturgical-primary)] data-[state=checked]:border-[var(--liturgical-primary)]"
                      data-testid="register-terms-checkbox"
                    />
                    <label 
                      htmlFor="terms-agreement" 
                      className="text-xs text-[#6B665E] font-['Nunito'] leading-relaxed cursor-pointer"
                    >
                      I agree to the{" "}
                      <Link to="/terms" target="_blank" className="text-[var(--liturgical-primary)] hover:underline font-medium">
                        Terms of Service
                      </Link>
                      ,{" "}
                      <Link to="/privacy" target="_blank" className="text-[var(--liturgical-primary)] hover:underline font-medium">
                        Privacy Policy
                      </Link>
                      , and{" "}
                      <Link to="/community-guidelines" target="_blank" className="text-[var(--liturgical-primary)] hover:underline font-medium">
                        Community Guidelines
                      </Link>
                    </label>
                  </div>
                  
                  <Button
                    type="submit"
                    className="w-full bg-[var(--liturgical-primary)] hover:brightness-90 text-white font-['Nunito'] font-semibold h-11 active:scale-95 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                    disabled={loading || !agreedToTerms}
                    data-testid="register-submit-btn"
                  >
                    {loading ? "Creating account..." : "Create Account"}
                  </Button>
                </form>
              </>
            )}

            {/* Message when no valid invite */}
            {inviteValid !== true && (
              <div className="text-center py-4 bg-[#F9F7F2] rounded-lg">
                <p className="text-sm text-[#6B665E] font-['Nunito']">
                  Don't have an invite code?
                </p>
                <p className="text-xs text-[#6B665E] font-['Nunito'] mt-1">
                  Ask a current Parish member to invite you.
                </p>
              </div>
            )}

            <p className="text-center text-sm text-[#6B665E] font-['Nunito']">
              Already have an account?{" "}
              <Link to="/login" className="text-[var(--liturgical-primary)] font-semibold hover:underline" data-testid="register-login-link">Sign in</Link>
            </p>
          </CardContent>
        </Card>
        
        {/* Legal Links */}
        <div className="mt-6 flex flex-wrap justify-center gap-x-4 gap-y-1 text-xs text-[#6B665E] font-['Nunito']">
          <Link to="/privacy" className="hover:text-[#2D2A26] transition-colors">Privacy</Link>
          <Link to="/terms" className="hover:text-[#2D2A26] transition-colors">Terms</Link>
          <Link to="/cookies" className="hover:text-[#2D2A26] transition-colors">Cookies</Link>
        </div>
        </div>
      </div>
    </div>
  );
}
